import { ref, onMounted, onUnmounted } from 'vue'

/**
 * Composable for lazy loading images with Intersection Observer
 * @param {Object} options - Configuration options
 * @param {string} options.rootMargin - Root margin for intersection observer
 * @param {number} options.threshold - Threshold for intersection observer
 * @returns {Object} - Reactive refs and methods for lazy loading
 */
export function useLazyImage(options = {}) {
  const {
    rootMargin = '50px',
    threshold = 0.01
  } = options

  const imageRef = ref(null)
  const isLoaded = ref(false)
  const isIntersecting = ref(false)
  const error = ref(false)
  let observer = null

  const loadImage = () => {
    if (!imageRef.value || isLoaded.value) return

    const img = imageRef.value
    const src = img.dataset.src

    if (!src) return

    img.src = src

    img.onload = () => {
      isLoaded.value = true
      img.classList.add('loaded')
    }

    img.onerror = () => {
      error.value = true
      img.classList.add('error')
    }
  }

  onMounted(() => {
    if (!imageRef.value) return

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              isIntersecting.value = true
              loadImage()
              if (observer && entry.target) {
                observer.unobserve(entry.target)
              }
            }
          })
        },
        {
          rootMargin,
          threshold
        }
      )

      observer.observe(imageRef.value)
    } else {
      loadImage()
    }
  })

  onUnmounted(() => {
    if (observer && imageRef.value) {
      observer.unobserve(imageRef.value)
      observer.disconnect()
    }
  })

  return {
    imageRef,
    isLoaded,
    isIntersecting,
    error,
    loadImage
  }
}

/**
 * Directive for lazy loading images
 * Usage: v-lazy-load="imageSrc"
 */
export const vLazyLoad = {
  mounted(el, binding) {
    const loadImage = () => {
      el.src = binding.value
      el.classList.add('lazy-loaded')
    }

    const createObserver = () => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              loadImage()
              observer.unobserve(el)
            }
          })
        },
        {
          rootMargin: '50px',
          threshold: 0.01
        }
      )

      observer.observe(el)
    }

    if ('IntersectionObserver' in window) {
      createObserver()
    } else {
      loadImage()
    }
  }
}

